import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Edit, Archive, Trash2, Plus, RefreshCw, Bookmark } from 'lucide-react';
import BrandBadge from './BrandBadge';
import BlacklistAlert from './BlacklistAlert';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  getAllOrders, 
  deleteOrder,
  archiveOrder,
  formatDate,
  getOrderTypeDisplayText,
  getStatusColor,
  confirmArchiveOrder
} from '../lib/orderService';
import { getBulkClientBlacklistStatusFromCache } from '../lib/blacklistService';
import { getAllClients } from '../lib/clientService';
import { conditionalLog } from '../lib/consoleLogger';

const ActiveOrders = () => {
  const { userRole, showCacheRefreshButtons } = useAuth();
  const [orders, setOrders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState({});
  const [clientBlacklistStatus, setClientBlacklistStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchActiveOrders();
    fetchBrands();
    fetchClients();
  }, []);

  // Removed aggressive window focus refresh to prevent reloading on tab switch

  const fetchActiveOrders = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      
      // Get all orders and filter only active (non-archived) ones
      const ordersList = await getAllOrders(forceRefresh);
      const activeOrders = ordersList.filter(order => !order.isArchived);
      
      // Sort by folio (descending - highest to lowest)
      const sortedActiveOrders = activeOrders.sort((a, b) => {
        const folioA = parseInt(a.folio) || 0;
        const folioB = parseInt(b.folio) || 0;
        return folioB - folioA; // Descending order
      });
      
      setOrders(sortedActiveOrders);
      
      // Load blacklist status from cached data (no Firestore queries)
      try {
        await loadClientBlacklistStatusFromCache(sortedActiveOrders);
      } catch (blacklistError) {
        console.error('Error loading blacklist status, continuing without it:', blacklistError);
        // Don't fail the entire operation if blacklist loading fails
      }
    } catch (error) {
      console.error('Error fetching active orders:', error);
      conditionalLog('Orders collection may not exist yet, starting with empty list');
      setOrders([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const loadClientBlacklistStatusFromCache = async (ordersList) => {
    try {
      // Extract unique client IDs from orders
      const clientIds = [...new Set(
        ordersList
          .map(order => order.cliente)
          .filter(Boolean) // Remove null/undefined values
      )];

      if (clientIds.length === 0) {
        conditionalLog('No client IDs found for blacklist status loading');
        return;
      }

      conditionalLog(`Loading blacklist status for ${clientIds.length} clients...`);
      // Get blacklist status from IndexedDB cache (no Firestore queries)
      const blacklistStatus = await getBulkClientBlacklistStatusFromCache(clientIds);
      setClientBlacklistStatus(blacklistStatus);
      conditionalLog('Blacklist status loaded successfully');
      
    } catch (error) {
      console.error('Error loading cached client blacklist status for active orders:', error);
      // Continue without blacklist status if it fails
    }
  };

  const fetchBrands = async () => {
    try {
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      conditionalLog('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  const fetchClients = async () => {
    try {
      const clientsList = await getAllClients();
      // Convert to a map for faster lookup by ID
      const clientsMap = {};
      clientsList.forEach(client => {
        clientsMap[client.id] = client;
      });
      setClients(clientsMap);
    } catch (error) {
      conditionalLog('Error fetching clients for contact notes:', error);
      setClients({});
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier || brands.length === 0) {
      return { nombre: identifier || 'Sin marca', color: '#6B7280' };
    }
    
    // Try to find by ID first (new format)
    let brand = brands.find(b => b.id === identifier);
    
    // If not found, try by name (backward compatibility)
    if (!brand) {
      brand = brands.find(b => b.nombre === identifier);
    }
    
    // Return found brand or fallback
    return brand || { nombre: identifier, color: '#6B7280' };
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 text-blue-700',
      'Ensamble': 'bg-purple-50 text-purple-700',
      'Garantía': 'bg-orange-50 text-orange-700',
      'Fabricación': 'bg-gray-50 text-gray-700'
    };
    
    return typeColors[tipo] || 'bg-gray-50 text-gray-700';
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      'Sin cargo': 'bg-gray-50 text-gray-700',
      'Esperando pago': 'bg-red-50 text-red-700',
      'Pagado': 'bg-green-50 text-green-700'
    };
    
    return statusColors[status] || 'bg-gray-50 text-gray-700';
  };

  const handleArchive = async (order) => {
    try {
      // Use shared validation and confirmation function
      const confirmed = await confirmArchiveOrder(order);
      if (!confirmed) {
        return;
      }

      await archiveOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido archivada`);
      fetchActiveOrders(true); // Force refresh to get updated data immediately
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error archiving order:', error);
      setError('Error al archivar la orden');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleForceRefresh = async () => {
    try {
      setIsRefreshing(true);
      // Force refresh both orders and brands from Firestore
      await Promise.all([
        fetchActiveOrders(true), // Force refresh from Firestore
        fetchBrands() // Also refresh brands to get updated names
      ]);
    } catch (error) {
      console.error('Error refreshing cache:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDelete = async (order) => {
    try {
      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente la orden ${order.folio}?\n\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar la orden en su lugar.`
      )) {
        return;
      }

      await deleteOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido eliminada permanentemente`);
      fetchActiveOrders();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting order:', error);
      setError('Error al eliminar la orden');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getClientDisplayName = (order) => {
    if (order.clienteName) {
      return order.clienteName;
    }
    if (order.cliente) {
      return `Cliente: ${order.cliente}`;
    }
    return 'Sin cliente';
  };

  const getContactNote = (order) => {
    if (!order.cliente || !clients[order.cliente]) {
      return null;
    }
    
    const client = clients[order.cliente];
    if (!client.contactos || client.contactos.length === 0) {
      return null;
    }
    
    // Get the principal contact or first contact
    const principalContact = client.contactos.find(contact => contact.esPrincipal) || client.contactos[0];
    return principalContact?.nota || null;
  };

  const renderClientInfo = (order) => {
    // Get client blacklist status
    const clientStatus = clientBlacklistStatus[order.cliente] || {};
    const clientForAlert = {
      isBlacklisted: clientStatus.isBlacklisted || false,
      activeIncidentsCount: clientStatus.activeIncidentsCount || 0,
      mostRecentIncidentMotivo: clientStatus.mostRecentIncidentMotivo || null
    };

    // Get contact note
    const contactNote = getContactNote(order);

    // For company clients, display company name bold, contact name, and phone on separate lines
    if (order.clienteCompanyName && order.clienteContactName) {
      return (
        <div className="max-w-xs">
          <div className="flex items-center space-x-2">
            <BlacklistAlert client={clientForAlert} size="sm" />
            <div className="text-sm font-bold leading-tight truncate text-gray-900 dark:text-white" title={order.clienteCompanyName}>
              {order.clienteCompanyName}
            </div>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 leading-tight truncate" title={order.clienteContactName}>
            {order.clienteContactName}
          </div>
          {order.clientePhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {order.clientePhone}
            </div>
          )}
          {contactNote && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={contactNote}>
              ({contactNote})
            </div>
          )}
        </div>
      );
    }
    
    // For individual clients or fallback, use the existing display
    return (
      <div className="max-w-xs">
        <div className="flex items-center space-x-2">
          <BlacklistAlert client={clientForAlert} size="sm" />
          <div className="text-sm leading-tight truncate text-gray-900 dark:text-white" title={getClientDisplayName(order)}>
            {getClientDisplayName(order)}
          </div>
        </div>
        {order.clientePhone && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {order.clientePhone}
          </div>
        )}
        {contactNote && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={contactNote}>
            ({contactNote})
          </div>
        )}
      </div>
    );
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    
    let orderDate;
    if (date instanceof Date) {
      orderDate = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firestore Timestamp
      orderDate = date.toDate();
    } else if (typeof date === 'string') {
      // String date from IndexedDB
      orderDate = new Date(date);
    } else if (date && typeof date === 'object' && date.seconds) {
      // Serialized Firestore Timestamp from IndexedDB
      orderDate = new Date(date.seconds * 1000);
    } else {
      // Fallback
      orderDate = new Date(date);
    }
    
    if (isNaN(orderDate.getTime())) {
      return '';
    }
    
    const now = new Date();
    const diffInMs = now - orderDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return '(hoy)';
    } else if (diffInDays === 1) {
      return '(ayer)';
    } else if (diffInDays < 7) {
      return `(hace ${diffInDays} días)`;
    } else if (diffInDays < 14) {
      return '(hace 1 semana)';
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `(hace ${weeks} semanas)`;
    } else if (diffInDays < 60) {
      return '(hace 1 mes)';
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `(hace ${months} meses)`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return years === 1 ? '(hace 1 año)' : `(hace ${years} años)`;
    }
  };

  const getEquipmentSummary = (order) => {
    if (!order.equipos || order.equipos.length === 0) {
      return 'Sin equipos';
    }
    
    return order.equipos.map((equipo, index) => {
      if (equipo.type === 'exempt' || !equipo.catalogData) {
        // For exempt equipment: descripcion, series with S/N label
        return (
          <div key={index} className="text-xs leading-tight break-words">
            <span>{equipo.descripcion}</span>
            {equipo.series && (
              <span> S/N: {equipo.series}</span>
            )}
          </div>
        );
      } else {
        // For catalog equipment: type, brand, model (bold), part number (parenthesis), S/N: serial
        const catalogData = equipo.catalogData;
        return (
          <div key={index} className="text-xs leading-tight break-words">
            {catalogData?.tipo && <span>{catalogData.tipo} </span>}
            {catalogData?.marca && <BrandBadge brand={getBrandByNameOrId(catalogData.marca)} size="xs" className="mr-1" />}
            {catalogData?.modelo && <span className="font-bold">{catalogData.modelo}</span>}
            {catalogData?.numeroParte && <span> ({catalogData.numeroParte})</span>}
            {equipo.series && <span> S/N: {equipo.series}</span>}
          </div>
        );
      }
    });
  };

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Órdenes Activas</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({orders.length} {orders.length === 1 ? 'orden' : 'órdenes'})
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Cache Refresh Button */}
            {showCacheRefreshButtons && (
              <button
                onClick={handleForceRefresh}
                disabled={isRefreshing}
                className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Actualizar datos desde servidor"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            {canEdit() && (
              <Link 
                to="/orders/new?from=dashboard"
                className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Nueva Orden"
              >
                <Plus className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mx-6 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
          {success}
        </div>
      )}

      {/* Orders List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando órdenes activas...
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No hay órdenes activas registradas
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {/* Desktop headers */}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Folio
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Fecha
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Tipo
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Cliente
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Equipos
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-48 hidden md:table-cell">
                    Estado
                  </th>
                  
                  {/* Mobile headers */}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider md:hidden">
                    Orden
                  </th>
                  
                  {canEdit() && (
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {orders.map((order) => (
                  <tr key={order.id} className={`cursor-pointer ${
                        order.isArchived 
                          ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => window.location.href = `/orders/${order.id}?from=dashboard`}>
                    
                    {/* Desktop columns */}
                    <td className="px-3 py-4 text-sm font-medium text-gray-900 dark:text-white hidden md:table-cell">
                      <div className="flex items-center space-x-2">
                        <div className="font-semibold">
                          {order.folio}
                        </div>
                        {order.isBookmarked && (
                          <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                        )}
                      </div>
                      {order.isArchived && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 mt-1">
                          Archivada
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                      <div>
                        {formatDate(order.fecha)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {getRelativeTime(order.fecha)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm hidden md:table-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                        {getOrderTypeDisplayText(order.tipo_de_orden)}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                      {renderClientInfo(order)}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                      <div className="max-w-xs">
                        <div className="space-y-1">
                          {getEquipmentSummary(order)}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm w-48 hidden md:table-cell">
                      <div className="space-y-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                          {order.status_orden || 'Sin estado'}
                        </span>
                        <div>
                          {order.estatus_pago ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                              {order.estatus_pago}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Mobile "Orden" column - contains all info */}
                    <td className="px-3 py-4 text-sm md:hidden">
                      <div className="space-y-2">
                        {/* Folio and archived status */}
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {order.folio}
                            </div>
                            {order.isBookmarked && (
                              <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                              {getOrderTypeDisplayText(order.tipo_de_orden)}
                            </span>
                            {order.isArchived && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                Archivada
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Date */}
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Fecha:</span> {formatDate(order.fecha)} {getRelativeTime(order.fecha)}
                        </div>
                        
                        {/* Client */}
                        <div className="flex items-center space-x-2 text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Cliente:</span>
                          <BlacklistAlert client={{
                            isBlacklisted: clientBlacklistStatus[order.cliente]?.isBlacklisted || false,
                            activeIncidentsCount: clientBlacklistStatus[order.cliente]?.activeIncidentsCount || 0,
                            mostRecentIncidentMotivo: clientBlacklistStatus[order.cliente]?.mostRecentIncidentMotivo || null
                          }} size="sm" />
                          <span className="text-gray-900 dark:text-white">{getClientDisplayName(order)}</span>
                        </div>
                        
                        {/* Equipment */}
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Equipos:</span>
                          <div className="text-gray-900 dark:text-white mt-1">
                            {getEquipmentSummary(order)}
                          </div>
                        </div>
                        
                        {/* Status */}
                        <div className="flex flex-wrap gap-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                            {order.status_orden || 'Sin estado'}
                          </span>
                          {order.estatus_pago ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                              {order.estatus_pago}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin estatus de pago</span>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Actions column */}
                    {canEdit() && (
                      <td className="px-2 py-4 text-sm font-medium">
                        <div className="flex flex-col space-y-1" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/orders/${order.id}/edit?from=dashboard`}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded flex justify-center"
                            title="Editar orden"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleArchive(order)}
                            className="p-2 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded flex justify-center"
                            title="Archivar orden"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          {canDelete() && (
                            <button
                              onClick={() => handleDelete(order)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded flex justify-center"
                              title="Eliminar orden"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveOrders;